import { css } from 'styled-components';

import { useActivityBuilderFontStyles } from 'approot/activity-builder/theme/activity-builder-theme.style';
import {
  ACTIVITIES_FONT_1_2,
  ACTIVITIES_FONT_3_4,
  ACTIVITIES_FONT_5_6,
  ACTIVITIES_FONT_K,
  ACTIVITIES_OPTION_SIZE_HUGE,
  ACTIVITIES_OPTION_SIZE_LARGE,
  ACTIVITIES_OPTION_SIZE_MEDIUM,
  InteractiveContentOptionSize,
} from 'global/constants';
import { useContext } from 'react';
import { ActivityBuilderThemeContext } from 'approot/activity-builder/theme/activity-builder-theme';
import {
  FONT_CALIBRE_SCHOOL,
  FONT_NSW_HANDWRITING,
} from 'approot/shared/styles/typography.style';
import { rem } from 'approot/shared/style';

export type FontSize = InteractiveContentOptionSize;

export type FontFamily = string | null;

interface FontSizes {
  fontSize: number;
  lineHeight: number;
}

type FontMap = {
  [key in number]: {
    [key in string]: {
      [key in FontSize]: FontSizes;
    };
  };
};

const fontMap: FontMap = {
  2: {
    [FONT_NSW_HANDWRITING]: {
      [ACTIVITIES_OPTION_SIZE_MEDIUM]: {
        fontSize: 32,
        lineHeight: 42,
      },
      [ACTIVITIES_OPTION_SIZE_LARGE]: {
        fontSize: 48,
        lineHeight: 56,
      },
      [ACTIVITIES_OPTION_SIZE_HUGE]: {
        fontSize: 64,
        lineHeight: 92,
      },
    },
    [FONT_CALIBRE_SCHOOL]: {
      [ACTIVITIES_OPTION_SIZE_MEDIUM]: {
        fontSize: 30,
        lineHeight: 42,
      },
      [ACTIVITIES_OPTION_SIZE_LARGE]: {
        fontSize: 42,
        lineHeight: 56,
      },
      [ACTIVITIES_OPTION_SIZE_HUGE]: {
        fontSize: 56,
        lineHeight: 72,
      },
    },
  },
  4: {
    [FONT_NSW_HANDWRITING]: {
      [ACTIVITIES_OPTION_SIZE_MEDIUM]: {
        fontSize: 28,
        lineHeight: 32,
      },
      [ACTIVITIES_OPTION_SIZE_LARGE]: {
        fontSize: 40,
        lineHeight: 48,
      },
      [ACTIVITIES_OPTION_SIZE_HUGE]: {
        fontSize: 52,
        lineHeight: 56,
      },
    },
    [FONT_CALIBRE_SCHOOL]: {
      [ACTIVITIES_OPTION_SIZE_MEDIUM]: {
        fontSize: 26,
        lineHeight: 32,
      },
      [ACTIVITIES_OPTION_SIZE_LARGE]: {
        fontSize: 36,
        lineHeight: 48,
      },
      [ACTIVITIES_OPTION_SIZE_HUGE]: {
        fontSize: 48,
        lineHeight: 56,
      },
    },
  },
  5: {
    [FONT_NSW_HANDWRITING]: {
      [ACTIVITIES_OPTION_SIZE_MEDIUM]: {
        fontSize: 24,
        lineHeight: 32,
      },
      [ACTIVITIES_OPTION_SIZE_LARGE]: {
        fontSize: 36,
        lineHeight: 44,
      },
      [ACTIVITIES_OPTION_SIZE_HUGE]: {
        fontSize: 48,
        lineHeight: 52,
      },
    },
    [FONT_CALIBRE_SCHOOL]: {
      [ACTIVITIES_OPTION_SIZE_MEDIUM]: {
        fontSize: 22,
        lineHeight: 32,
      },
      [ACTIVITIES_OPTION_SIZE_LARGE]: {
        fontSize: 32,
        lineHeight: 44,
      },
      [ACTIVITIES_OPTION_SIZE_HUGE]: {
        fontSize: 44,
        lineHeight: 52,
      },
    },
  },
};

export const getFontSizes = (
  originalFontSize: number | undefined,
  fontFamily: FontFamily = 'calibre',
  fontSize: FontSize
): FontSizes => {
  const selectedFontFamily = fontFamily || 'calibre';

  if (
    originalFontSize === ACTIVITIES_FONT_1_2 ||
    originalFontSize === ACTIVITIES_FONT_K
  ) {
    return fontMap[2][selectedFontFamily][fontSize];
  }
  if (originalFontSize === ACTIVITIES_FONT_3_4) {
    return fontMap[4][selectedFontFamily][fontSize];
  }
  if (originalFontSize === ACTIVITIES_FONT_5_6) {
    return fontMap[5][selectedFontFamily][fontSize];
  }

  return fontMap[2][selectedFontFamily][fontSize];
};

export const useFillInBlankFontSizes = ({
  fontFamily,
  size = ACTIVITIES_OPTION_SIZE_MEDIUM,
}: {
  fontFamily: FontFamily;
  size?: FontSize | null;
}) => {
  const { originalFontSize } = useContext(ActivityBuilderThemeContext);

  const baseFontCss = useActivityBuilderFontStyles({
    isSmall: false,
  });

  const props = getFontSizes(originalFontSize, fontFamily, size);

  return size === null
    ? baseFontCss
    : css`
        font-size: ${rem(props.fontSize)};
        line-height: ${rem(props.lineHeight)};
        min-height: ${rem(props.lineHeight)};
      `;
};
